import { useNavigate, useLocation } from 'react-router-dom'

import type { Agent } from '../../services/agentService'
import { ChatIcon, ChatIconGradient } from '../icons/ChatIcon'
import { RagIcon, RagIconGradient } from '../icons/RagIcon'
import { TreeIcon, TreeIconGradient } from '../icons/TreeIcon'
import { ConnectIcon, ConnectIconGradient } from '../icons/ConnectIcon'
import { SkillsIcon, SkillsIconGradient } from '../icons/SkillsIcon'
import { BaseIcon, BaseIconGradient } from '../icons/BaseIcon'
import { Avatar } from '../ui/Avatar' // Assuming Avatar component is in '../ui/Avatar'

interface AgentSidebarProps {
    agent: Agent | null
    onChatClick?: () => void
}

export function AgentSidebar({ agent, onChatClick }: AgentSidebarProps) {
    // MOCK FOR VERIFICATION: Force long name
    // const displayAgent = { ...agent, name: "Angelo Gustavo de Oliveira" }
    // ACTUALLY: Let's just use the prop but I'll change the render temporarily
    const navigate = useNavigate()
    const location = useLocation()

    const navItems = [
        {
            label: 'Chat',
            tooltip: 'Conversar',
            icon: ChatIcon,
            hoverIcon: ChatIconGradient,
            path: '#', // Handled by onClick
            bgAction: true, // Marker for special action
            disabled: !agent
        },
        {
            label: 'RAG',
            tooltip: 'Configuração Cognitiva',
            icon: RagIcon,
            hoverIcon: RagIconGradient,
            path: agent ? `/rag/${agent.handle}` : '#',
            disabled: !agent
        },
        {
            label: 'Tree', // Renamed from Configuração
            tooltip: 'Editor de Arquivos',
            icon: TreeIcon, // Replaced TreeIcon for consistency (clean outline)
            hoverIcon: TreeIconGradient,
            path: agent ? `/agentes/${agent.handle}/configuracao` : '#',
            disabled: !agent
        },
        {
            label: 'Connect', // Renamed from Integrações
            tooltip: 'Conectar (Em breve)',
            icon: ConnectIcon,
            hoverIcon: ConnectIconGradient,
            path: '/integracoes', // Future route
            disabled: true
        },
        {
            label: 'Skills',
            tooltip: 'Skills (Em breve)',
            icon: SkillsIcon,
            hoverIcon: SkillsIconGradient,
            path: '/skills', // Future route
            disabled: true
        },
        {
            label: 'Base', // Renamed from Conhecimento
            tooltip: 'Base de conhecimentos',
            icon: BaseIcon,
            hoverIcon: BaseIconGradient,
            path: '/conhecimento', // Future route
            disabled: true
        }
    ]

    const handleNavigation = (item: typeof navItems[0]) => {
        if (item.disabled) {
            // Placeholder for future functionality
            alert('Funcionalidade em desenvolvimento: ' + item.label)
            return
        }

        // Handle special Chat action
        if ('bgAction' in item && item.bgAction && item.label === 'Chat' && onChatClick) {
            onChatClick()
            return
        }

        navigate(item.path)
    }

    return (
        <div className="w-[72px] h-full bg-white dark:bg-black flex flex-col items-center py-4 z-40">
            {/* Avatar Home Button */}
            <button
                onClick={() => agent && navigate(`/agentes/${agent.handle}`)}
                className="mb-8 relative group"
                title={agent ? "Voltar ao Perfil" : "Carregando..."}
                disabled={!agent}
            >
                <div className="w-12 h-12 relative flex items-center justify-center">
                    {/* Cosmic Pulse Glow */}
                    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[140%] h-[140%] z-0 pointer-events-none flex items-center justify-center">
                        <div className="w-full h-full rounded-full bg-gradient-to-tr from-[#f97316] via-[#a855f7] to-[#3b82f6] blur-xl opacity-40 animate-cosmic-pulse" />
                    </div>

                    {/* Avatar */}
                    {agent ? (
                        <Avatar
                            src={agent.avatar_url ?? undefined}
                            alt={agent.name}
                            size="md"
                            className="relative z-10 ring-2 ring-white dark:ring-black group-hover:ring-4 transition-all duration-300"
                        />
                    ) : (
                        <div className="w-12 h-12 rounded-full bg-gray-200 dark:bg-gray-800 animate-pulse relative z-10"></div>
                    )}

                    {/* Name Badge - Always visible */}
                    <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 flex items-center gap-0.5 bg-white dark:bg-black px-1.5 py-0.5 rounded-full shadow-sm border border-gray-100 dark:border-gray-800 whitespace-nowrap z-50 max-w-[68px]">
                        {agent ? (
                            <>
                                <span
                                    className="text-[9px] font-medium text-gray-900 dark:text-white leading-none truncate block max-w-full"
                                    title={agent.name}
                                >
                                    {agent.name}
                                </span>
                                {/* Twitter-style verified badge - Tiny */}
                                <svg className="w-2.5 h-2.5 text-[#1d9bf0] flex-shrink-0" viewBox="0 0 22 22" aria-label="Verified account" role="img">
                                    <g>
                                        <path fill="currentColor" d="M20.396 11c-.018-.646-.215-1.275-.57-1.816-.354-.54-.852-.972-1.438-1.246.223-.607.27-1.264.14-1.897-.131-.634-.437-1.218-.882-1.687-.47-.445-1.053-.75-1.687-.882-.633-.13-1.29-.083-1.897.14-.273-.587-.704-1.086-1.245-1.44S11.647 1.62 11 1.604c-.646.017-1.273.213-1.813.568s-.969.854-1.24 1.44c-.608-.223-1.267-.272-1.902-.14-.635.13-1.22.436-1.69.882-.445.47-.749 1.055-.878 1.688-.13.633-.08 1.29.144 1.896-.587.274-1.087.705-1.443 1.245-.356.54-.555 1.17-.574 1.817.02.647.218 1.276.574 1.817.356.54.856.972 1.443 1.245-.224.606-.274 1.263-.144 1.896.13.634.433 1.218.877 1.688.47.443 1.054.747 1.687.878.633.132 1.29.084 1.897-.136.274.586.705 1.084 1.246 1.439.54.354 1.17.551 1.816.569.647-.016 1.276-.213 1.817-.567s.972-.854 1.245-1.44c.604.239 1.266.296 1.903.164.636-.132 1.22-.447 1.68-.907.46-.46.776-1.044.908-1.681s.075-1.299-.165-1.903c.586-.274 1.084-.705 1.439-1.246.354-.54.551-1.17.569-1.816zM9.662 14.85l-3.429-3.428 1.293-1.302 2.072 2.072 4.4-4.794 1.347 1.246z"></path>
                                    </g>
                                </svg>
                            </>
                        ) : (
                            <div className="w-8 h-2 bg-gray-200 dark:bg-gray-700 rounded animate-pulse"></div>
                        )}
                    </div>
                </div>
            </button>

            {/* Navigation Items */}
            <div className="flex flex-col gap-3 w-full px-2">
                {navItems.map((item) => {
                    const isActive = location.pathname === item.path
                    const Icon = item.icon
                    const HoverIcon = item.hoverIcon

                    return (
                        <button
                            key={item.label}
                            onClick={() => handleNavigation(item)}
                            className={`
                                relative flex flex-col items-center justify-center gap-0.5 p-2 rounded-lg transition-all group w-full duration-200
                                ${isActive
                                    ? 'bg-gray-100 dark:bg-white/10 text-black dark:text-white shadow-sm'
                                    : 'text-black dark:text-white hover:bg-gray-100 dark:hover:bg-white/5'
                                }
                            `}
                        // Removed native title to use custom tooltip
                        // title={item.label} 
                        >
                            {/* Icon Container with relative positioning for overlap */}
                            <div className="relative w-5 h-5 mb-0.5">
                                {HoverIcon ? (
                                    <>
                                        <Icon className={`w-5 h-5 absolute inset-0 transition-opacity duration-300 stroke-[1.25] ${isActive ? 'opacity-100' : 'opacity-80 group-hover:opacity-0'}`} />
                                        <HoverIcon className={`w-5 h-5 absolute inset-0 transition-opacity duration-300 stroke-[1.25] ${isActive ? 'opacity-0' : 'opacity-0 group-hover:opacity-100'}`} />
                                    </>
                                ) : (
                                    <Icon className={`w-5 h-5 stroke-[1.25] transition-all ${isActive ? '' : 'opacity-80 group-hover:opacity-100 duration-300 ease-out group-hover:scale-110 group-hover:-translate-y-0.5'}`} />
                                )}
                            </div>

                            {/* Text - Full width, no truncate, small but readable */}
                            <span className={`text-[10px] text-center leading-tight tracking-wide transition-colors ${isActive ? 'font-medium' : 'font-normal'}`}>
                                {item.label}
                            </span>

                            {/* Custom Tooltip - Positioned to the right */}
                            <div className="absolute left-full top-1/2 -translate-y-1/2 ml-3 px-3 py-1.5 bg-white dark:bg-black text-gray-700 dark:text-white text-xs rounded-md shadow-lg border border-gray-200 dark:border-gray-800 whitespace-nowrap opacity-0 group-hover:opacity-100 visible pointer-events-none transition-all duration-200 z-50">
                                {item.tooltip || item.label}
                                {/* Arrow pointing left */}
                                <div className="absolute top-1/2 -left-1 -translate-y-1/2 w-2 h-2 bg-white dark:bg-black border-l border-b border-gray-200 dark:border-gray-800 transform rotate-45"></div>
                            </div>
                        </button>
                    )
                })}
            </div>
        </div>
    )
}
